// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ListLineContainer, { ListLineContainerStyles } from "@awc/App/Components/MyListsDetails/ListLineContainer";
import Notify from "@awc/App/Components/Notify/Notify";
import { QtyFieldStyles } from "@awc/App/Components/Qty/QtyField";
import { TextAreaProps } from "@awc/MobiusOverrides/TextArea/TextArea";
import ProductQuantityOrdered from "@awc/Overrides/Components/ProductQuantityOrdered";
import { sharedDateTimeOptions } from "@awc/XDL/constants";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    isWishListLineDeactivated,
    isWishListLineDiscontinued,
    isWishListLineRestricted,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import setWishListLineIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetWishListLineIsSelected";
import updateQuantity from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateQuantity";
import updateUnitOfMeasure from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateUnitOfMeasure";
import updateWishListLine from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLine";
import updateWishListLineQuantities from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLineQuantities";
import translate from "@insite/client-framework/Translate";
import { ProductModel, WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import MyListDetailsDiscontinuedProductListLine from "@insite/content-library/Widgets/MyListsDetails/MyListDetailsDiscontinuedProductListLine";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import RefreshCw from "@insite/mobius/Icons/RefreshCw";
import { LoadingOverlay } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cloneDeep from "lodash/cloneDeep";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    wishList: WishListModel;
    wishListLine: WishListLineModel;
    productInfo: ProductInfo;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
    // XNG-Changes: add notes to onEditNotesClick
    onEditNotesClick: (wishListLine: WishListLineModel, notes: string) => void;
    showImageBadges: boolean;
    maximumNumberOfImageBadges: number;
    showTextBadges: boolean;
    maximumNumberOfTextBadges: number;
    onViewReplacementProduct?: (wishListLine: WishListLineModel) => void;
    canDrag?: boolean;
    DragHandle?: any;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSelected: !!state.pages.myListDetails.selectedWishListLineIds.find(o => o === ownProps.wishListLine.id),
    settingsCollection: getSettingsCollection(state),
    isQuantityUpdated: state.pages.myListDetails.wishListLinesWithUpdatedQuantity[ownProps.wishListLine.id],
    // XNG-Changes
    editingSortOrder: state.pages.myListDetails.editingSortOrder,
    language: state.context.session.language,
});

const mapDispatchToProps = {
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    updateWishListLineQuantities,
    // XNG-Changes
    updateWishListLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsProductListLineStyles {
    editNotesLink?: LinkPresentationProps;
    lineInnerContainer?: GridContainerProps;
    selectedCheckboxHidden?: HiddenProps;
    imageGridItem?: GridItemProps;
    selectedCheckbox?: CheckboxPresentationProps;
    imageWrapper?: InjectableCss;
    editNotesHidden?: HiddenProps;
    productImageStyles?: ProductImageStyles;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    notesStyles?: TypographyPresentationProps;
    overflowMenuGridItem?: GridItemProps;
    partNumbersHidden?: HiddenProps;
    partNumbersGridItem?: GridItemProps;
    priceAndAvailabilityGridItem?: GridItemProps;
    priceAndAvailabilityInnerContainer?: GridContainerProps;
    purchasedDateGridItem?: GridItemProps;
    purchasedDateLabelText?: TypographyPresentationProps;
    purchasedDateText?: TypographyPresentationProps;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    uomAndQuantityGridItem?: GridItemProps;
    uomAndQuantityInnerContainer?: GridContainerProps;
    uomGridItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    availabilityGridItem?: GridItemProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    notesHidden?: HiddenProps;
    notesGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    deleteLinkHidden?: HiddenProps;
    deleteLink?: LinkPresentationProps;
    updateQuantityWrapper?: InjectableCss;
    updateQuantityLink?: LinkPresentationProps;
    quantityUpdatedIcon?: IconPresentationProps;
    quantityUpdatedText?: TypographyPresentationProps;
    restrictedDescriptionGridItem?: GridItemProps;
    restrictedDescriptionText?: TypographyPresentationProps;
    restrictedMessageText?: TypographyPresentationProps;
    restrictedRemoveItemLink?: LinkPresentationProps;
    textBadgeStyles?: ProductTextBadgesStyles;
    imageBadgeStyles?: ProductImageBadgesStyles;
    // XNG-Changes
    sortOrderGridItem?: GridItemProps;
    sortOrderTextField?: TextFieldProps;
    quantityAddtoCartWrapper?: InjectableCss;
    quantityUpdatedWrapper?: InjectableCss;
    createdOnText?: TypographyPresentationProps;
    deleteItemWrapper?: InjectableCss;
    partNumbersSection?: InjectableCss;
    updateQTY?: InjectableCss;
    editableNotesTextArea?: TextAreaProps;
    productQuantityOrdered?: QtyFieldStyles;
    checkboxSortOrderWrapper?: InjectableCss;
    listLineContainerStyles?: ListLineContainerStyles;
    noteWrapper?: InjectableCss;
    noteLabel?: TypographyPresentationProps;
    noteTextFieldWrapper?: InjectableCss;
    editableNotesTextField?: TextFieldPresentationProps;
}

export const productListLineStyles: MyListsDetailsProductListLineStyles = {
    editNotesLink: {
        typographyProps: { size: 12 },
        css: css`
            margin-top: 5px;
        `,
    },
    lineInnerContainer: {
        gap: 10,
    },
    selectedCheckboxHidden: { below: "md" },
    imageGridItem: {
        width: [3, 3, 2, 2, 1],
        printWidth: 1,
    },
    selectedCheckbox: {
        sizeVariant: "small",
        css: css`
            @media print {
                display: none;
            }
            margin-right: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
            }
            width: 100%;
            min-width: 0;
            flex-direction: column;
        `,
    },
    editNotesHidden: { below: "md" },
    descriptionGridItem: {
        width: [8, 8, 4, 4, 4],
        printWidth: 6,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersStyles: {
        erpNumberGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        erpNumberLabelText: { variant: "legend" },
        customerNameGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        customerNameLabelText: { variant: "legend" },
        manufacturerItemGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        manufacturerItemLabelText: { variant: "legend" },
    },
    overflowMenuGridItem: {
        width: [1, 1, 0, 0, 0],
        printWidth: 0,
    },
    partNumbersHidden: {
        below: "md",
        css: css`
            @media print {
                display: block;
            }
            width: 100%;
        `,
    },
    partNumbersGridItem: { width: 12 },
    priceAndAvailabilityGridItem: {
        width: [12, 12, 4, 4, 5],
        printWidth: 5,
    },
    priceAndAvailabilityInnerContainer: { gap: 10 },
    purchasedDateGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    purchasedDateLabelText: {
        variant: "p",
        transform: "uppercase",
        weight: 600,
        css: css`
            @media print {
                font-size: 12px;
            }
        `,
    },
    priceGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    productPriceStyles: {
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            weight: 600,
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: { typographyProps: { size: 12 } },
    },
    uomAndQuantityGridItem: {
        width: [12, 12, 12, 12, 7],
        printWidth: 7,
    },
    uomAndQuantityInnerContainer: {
        gap: 10,
        css: css`
            @media print {
                input,
                select {
                    height: 18px !important;
                    font-size: 11px !important;
                }
            }
        `,
    },
    uomGridItem: { width: 9, printWidth: 6 },
    quantityGridItem: { width: 3, printWidth: 6 },
    productQuantityOrderedStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        cssOverrides: {
            inputSelect: css`
                @media print {
                    width: 100px;
                }
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        iconProps: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    availabilityGridItem: { width: 12 },
    buttonsGridItem: {
        width: [12, 12, 2, 2, 2],
        printWidth: 0,
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    notesHidden: {
        above: "sm",
        css: css`
            @media print {
                display: none;
            }
            width: 100%;
        `,
    },
    notesGridItem: { width: 12 },
    addToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    deleteLinkHidden: {
        below: "md",
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            align-items: center;
        `,
    },
    deleteLink: {
        typographyProps: { size: 12 },
        css: css`
            margin-top: 10px;
        `,
    },
    updateQuantityWrapper: {
        css: css`
            display: flex;
            margin-top: 10px;
        `,
    },
    updateQuantityLink: {
        typographyProps: { size: 12 },
        icon: {
            iconProps: {
                src: RefreshCw,
                size: 12,
            },
        },
    },
    quantityUpdatedIcon: {
        src: Check,
        size: 16,
        css: css`
            margin-right: 5px;
        `,
    },
    quantityUpdatedText: {
        size: 12,
    },

    textBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
    },
    imageBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
        imageItem: {
            css: css`
                margin: 5px 0;
            `,
        },
    },

    restrictedDescriptionGridItem: {
        width: [12, 12, 6, 6, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    restrictedMessageText: { color: "warning" },
};

const styles = productListLineStyles;

const MyListsDetailsProductListLine: React.FC<Props> = ({
    wishList,
    wishListLine,
    productInfo,
    isSelected,
    settingsCollection,
    isQuantityUpdated,
    onDeleteClick,
    onEditNotesClick,
    updateWishListLineQuantities,
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    showImageBadges,
    showTextBadges,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
    onViewReplacementProduct,
    // XNG-Changes
    editingSortOrder,
    updateWishListLine,
    language,
    canDrag,
    DragHandle,
}) => {
    const product = mapWishListLineToProduct(wishListLine);
    // XNG-Changes:add line notes
    const [showLineNote, setShowLineNote] = useState(wishListLine.notes.length > 0);
    const [lineNotes, setLineNotes] = useState(wishListLine.notes);
    // XNG-Changes: add spinner when changing UOM
    const [isLoading, setIsLoading] = React.useState(false);

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setWishListLineIsSelected({ wishListLineId: wishListLine.id, isSelected: value });
    };

    const onUpdateQuantityClick = () => {
        updateWishListLineQuantities({ reloadWishListLines: true });
    };

    // XNG-Changes: add spinner when changing UOM
    const hideLoader = () => {
        setIsLoading(false);
    };

    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    const { productSettings } = settingsCollection;
    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: qtyOrdered => {
            // XNG-Changes: skip updateQuantity if qty is the same (re-render), fixes "QTY updated" message disappear bug
            if (wishListLine.qtyOrdered === qtyOrdered && productInfo.qtyOrdered === qtyOrdered) {
                return;
            }
            updateQuantity({ wishListLineId: wishListLine.id, qtyOrdered });
        },
        onUnitOfMeasureChanged: unitOfMeasure => {
            // XNG-Changes: add spinner when changing UOM
            setIsLoading(true);
            updateUnitOfMeasure({
                wishListLineId: wishListLine.id,
                unitOfMeasure,
                // XNG-Changes: add spinner when changing UOM
                onSuccess: hideLoader,
                onComplete: hideLoader,
            });
        },
    };

    if (isWishListLineDiscontinued(wishListLine, productInfo) && wishListLine.isActive) {
        return (
            <MyListDetailsDiscontinuedProductListLine
                wishList={wishList}
                wishListLine={wishListLine}
                productContext={productContext}
                isSelected={isSelected}
                selectChangeHandler={selectChangeHandler}
                onDeleteClick={onDeleteClick}
                onViewReplacementProduct={onViewReplacementProduct}
            />
        );
    }

    if (isWishListLineRestricted(wishListLine, productInfo) || isWishListLineDeactivated(wishListLine)) {
        // XNG-Changes:use ListLineContainer custom component
        return (
            //     <GridContainer
            //     {...styles.lineInnerContainer}
            //     className="wishlist-restricted-line-grid-container"
            //     data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            // >
            //     <GridItem
            //         {...styles.restrictedDescriptionGridItem}
            //         className="wishlist-restricted-line-description-grid-item"
            //     >
            <ListLineContainer data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}>
                <ListLineContainer.Description>
                    <Typography
                        {...styles.restrictedDescriptionText}
                        className="wishlist-restricted-line-description-text"
                    >
                        {wishListLine.shortDescription}
                    </Typography>
                    {/* </GridItem>
                    <GridItem {...styles.partNumbersGridItem} className="wishlist-restricted-line-part-number-grid-item">
                     */}
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                    {/* </GridItem>
                <GridItem {...styles.priceAndAvailabilityGridItem}> */}
                    {/* XNG-Changes:use  Notify */}
                    <Notify
                        type="warning"
                        {...styles.restrictedMessageText}
                        className="wishlist-restricted-line-message-text"
                        data-test-selector="restrictedMessage"
                    >
                        {isWishListLineRestricted(wishListLine, productInfo) &&
                            !isWishListLineDeactivated(wishListLine) && (
                                <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}</>
                            )}
                        {isWishListLineDeactivated(wishListLine) && (
                            <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Deactivation")}</>
                        )}
                    </Notify>
                </ListLineContainer.Description>
                <ListLineContainer.DeleteItem>
                    {/* </GridItem>
                <GridItem {...styles.buttonsGridItem} className="wishlist-restricted-line-buttons-grid-item"> */}
                    {canEditWishList && (
                        <Link
                            className="wishlist-restricted-line-remove-link"
                            {...styles.deleteLink}
                            onClick={() => onDeleteClick(wishListLine)}
                            data-test-selector="removeRestricted"
                        >
                            {translate("Delete")}
                        </Link>
                    )}
                </ListLineContainer.DeleteItem>
            </ListLineContainer>
            //  </GridItem>
            //  </GridContainer>
        );
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    // XNG-Changes - start : sort order moved to this file
    const updateSortOrder = (wishListLine: WishListLineModel, value: number | string) => {
        const sortOrder = parseInt(value.toString() || "1", 10);
        if (wishListLine.sortOrder === sortOrder) {
            return;
        }
        const wishListLineToUpdate = { ...wishListLine, sortOrder };
        updateWishListLine({
            wishListId: wishList.id,
            wishListLineId: wishListLineToUpdate.id,
            wishListLine: wishListLineToUpdate,
        });
    };

    const sortOrderBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        updateSortOrder(wishListLine, event.target.value);
    };

    const sortOrderKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };
    const toggleLineNotesHandler = () => {
        setShowLineNote(!showLineNote);
    };
    const lineNotesChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLineNotes(event.currentTarget.value);
    };

    const lineNotesblurHandler = () => {
        if (wishListLine.notes !== lineNotes) {
            onEditNotesClick(wishListLine, lineNotes);
        }
    };
    // XNG-Changes: use ListLineContainer instead of Gridd  and styles changes and add LoadingOverlay
    return (
        <LoadingOverlay loading={isLoading}>
            <ProductContext.Provider value={productContext}>
                <ListLineContainer
                    data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
                    extendedStyles={styles.listLineContainerStyles}
                    {...{ editingSortOrder }}
                >
                    <ListLineContainer.Checbox>
                        <StyledWrapper {...styles.checkboxSortOrderWrapper} {...{ editingSortOrder }}>
                            <Checkbox
                                {...styles.selectedCheckbox}
                                checked={isSelected}
                                onChange={selectChangeHandler}
                                hiddenIconText={translate("Select")}
                                data-test-selector="myListDetails_selectRowCheckbox"
                            ></Checkbox>
                            {editingSortOrder && (
                                <>
                                    {canDrag && <DragHandle />}
                                    <StyledWrapper {...styles.sortOrderGridItem}>
                                        <TextField
                                            {...styles.sortOrderTextField}
                                            type="number"
                                            min={1}
                                            defaultValue={wishListLine.sortOrder}
                                            onBlur={sortOrderBlurHandler}
                                            onKeyPress={sortOrderKeyPressHandler}
                                        />
                                    </StyledWrapper>
                                </>
                            )}
                        </StyledWrapper>
                    </ListLineContainer.Checbox>
                    <ListLineContainer.Image>
                        <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    </ListLineContainer.Image>
                    <ListLineContainer.Description>
                        {product.brand && (
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        )}
                        <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                        <StyledWrapper {...styles.partNumbersSection}>
                            <ProductPartNumbers
                                productNumber={product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        </StyledWrapper>

                        {showTextBadges && textBadges && textBadges.length > 0 && (
                            <ProductTextBadges
                                badgeList={textBadges}
                                maxBadges={maximumNumberOfTextBadges ?? 4}
                                extendedStyles={styles.textBadgeStyles}
                            />
                        )}
                        {showImageBadges && imageBadges && imageBadges.length > 0 && (
                            <ProductImageBadges
                                badgeList={imageBadges}
                                maxBadges={maximumNumberOfImageBadges ?? 4}
                                extendedStyles={styles.imageBadgeStyles}
                                numberOfColumns={4}
                            />
                        )}
                        <StyledWrapper {...styles.deleteItemWrapper}>
                            <Typography {...styles.createdOnText}>
                                {`${translate("Added")} `}
                                {getLocalizedDateTime({
                                    dateTime: new Date(wishListLine.createdOn),
                                    language,
                                    options: {
                                        ...sharedDateTimeOptions,
                                    },
                                })}
                            </Typography>
                        </StyledWrapper>
                    </ListLineContainer.Description>
                    <ListLineContainer.Notes>
                        {canEditWishList && (
                            <StyledWrapper {...styles.noteWrapper}>
                                <Typography
                                    {...styles.noteLabel}
                                    htmlFor={`notes-${wishListLine.id}`}
                                    forwardAs="label"
                                    onClick={toggleLineNotesHandler}
                                    data-test-selector="wishListLine_notesShowHideButton"
                                >
                                    <button>{translate("Line Notes")}</button>
                                </Typography>
                                {/* XNG-Changes */}
                                {canEditWishList && (
                                    <Link
                                        {...styles.deleteLink}
                                        className="wishlist-line-delete-link"
                                        onClick={() => onDeleteClick(wishListLine)}
                                        data-test-selector="deleteListItem"
                                    >
                                        {translate("Delete")}
                                    </Link>
                                )}
                                {showLineNote && (
                                    <StyledWrapper {...styles.noteTextFieldWrapper}>
                                        {/* XNG-Change use text field as in classic */}
                                        <TextField
                                            {...styles.editableNotesTextField}
                                            // XNG-Change: classic change
                                            label={translate("Add Item Notes")}
                                            value={lineNotes}
                                            id={`notes-${wishListLine.id}`}
                                            onChange={lineNotesChangeHandler}
                                            onBlur={lineNotesblurHandler}
                                            data-test-selector="wishListLine_notes"
                                        />
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        )}
                    </ListLineContainer.Notes>
                    <ListLineContainer.Price>
                        <>
                            {wishList.isAutogenerated && (
                                <GridItem
                                    {...styles.purchasedDateGridItem}
                                    className="wishlist-line-purchased-date-grid-container"
                                >
                                    <Typography
                                        {...styles.purchasedDateLabelText}
                                        className="wishlist-line-purchased-date-label-text"
                                    >
                                        {translate("Purchased Date")}
                                    </Typography>
                                    <Typography
                                        {...styles.purchasedDateText}
                                        className="wishlist-line-purchased-date-text"
                                        data-test-selector="purchasedDate"
                                    >
                                        <LocalizedDateTime dateTime={wishListLine.createdOn} />
                                    </Typography>
                                </GridItem>
                            )}
                            <ProductPrice
                                product={productContext}
                                showSavings={true}
                                showSavingsAmount={productSettings.showSavingsAmount}
                                showSavingsPercent={productSettings.showSavingsPercent}
                                // XNG-Changes
                                showLabel={false}
                                extendedStyles={styles.productPriceStyles}
                            />
                            {productSettings.showInventoryAvailability && wishListLine.trackInventory && (
                                <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                            )}
                            <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                        </>
                    </ListLineContainer.Price>
                    <ListLineContainer.Actions>
                        <StyledWrapper {...styles.quantityAddtoCartWrapper}>
                            <ProductQuantityOrdered extendedStyles={styles.productQuantityOrdered} />
                            {product.unitOfMeasures && product.unitOfMeasures.length > 1 && (
                                <ProductUnitOfMeasureSelect
                                    disabled={!canEditWishList}
                                    extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                />
                            )}
                            <ProductAddToCartButton
                                notes={wishListLine.notes}
                                extendedStyles={styles.addToCartButton}
                                data-test-selector="addToCart"
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.updateQTY}>
                            {canEditWishList && (
                                <>
                                    {productInfo.qtyOrdered > 0 && wishListLine.qtyOrdered !== productInfo.qtyOrdered && (
                                        <Link
                                            {...styles.updateQuantityLink}
                                            className="wishlist-line-update-quantity-link"
                                            onClick={() => onUpdateQuantityClick()}
                                        >
                                            {translate("Update QTY")}
                                        </Link>
                                    )}
                                    {isQuantityUpdated && wishListLine.qtyOrdered === productInfo.qtyOrdered && (
                                        <StyledWrapper {...styles.quantityUpdatedWrapper}>
                                            <Icon
                                                {...styles.quantityUpdatedIcon}
                                                className="wishlist-line-quantity-updated-icon"
                                            />
                                            <Typography
                                                {...styles.quantityUpdatedText}
                                                className="wishlist-line-quantity-updated-text"
                                            >
                                                {translate("QTY updated")}
                                            </Typography>
                                        </StyledWrapper>
                                    )}
                                </>
                            )}
                        </StyledWrapper>
                    </ListLineContainer.Actions>
                    <ListLineContainer.DeleteItem></ListLineContainer.DeleteItem>
                </ListLineContainer>
            </ProductContext.Provider>
        </LoadingOverlay>
    );
};

const mapWishListLineToProduct = (wishListLine: WishListLineModel) => {
    const product = cloneDeep(wishListLine) as any as ProductModel;
    product.id = wishListLine.productId;
    product.productNumber = wishListLine.erpNumber;
    product.productTitle = wishListLine.shortDescription;
    product.customerProductNumber = wishListLine?.customerName;
    product.unitOfMeasures =
        wishListLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;

    return product;
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsProductListLine);
